/**
 * Customer Form
 * - Create mode: guided step-by-step wizard (Basic Info -> Business Details -> Address -> Review)
 * - Edit mode: original tabbed layout, addresses managed via AddressManagement
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box, Paper, Typography, TextField, Grid, FormControl, InputLabel,
  Select, MenuItem, Tabs, Tab, Autocomplete, Chip, Stepper, Step, StepLabel,
  FormControlLabel, Checkbox, Button, Divider, Alert, Stack,
} from '@mui/material'
import {
  Save as SaveIcon, Person as PersonIcon,
  LocationOn as LocationIcon, Business as BusinessIcon,
  FactCheck as ReviewIcon, ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import {
  useCreateCustomerMutation, useUpdateCustomerMutation, useGetCustomerQuery,
  useGetCustomerGroupsQuery,
} from '../store/services/customerApi'
import { useGetCataloguesQuery } from '../store/services/catalogueApi'
import { CustomerCreateData, CustomerType } from '../types/customer'
import { AddressService } from '../services/addressService'
import { AddressType } from '../types/address'
import { isValidMobile, isValidGSTIN, isValidPAN, isValidPincode, isValidEmail } from '../utils/customerValidation'
import PageHeader from '../components/ui/PageHeader'
import AddressManagement from '../components/customers/AddressManagement'

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const WIZARD_STEPS = ['Basic Info', 'Business Details', 'Address', 'Review & Create']

const CustomerFormSimple: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const isEditing = Boolean(id)

  const [activeTab, setActiveTab] = useState(0)
  const [activeStep, setActiveStep] = useState(0)

  const [formData, setFormData] = useState<CustomerCreateData>({
    name: '',
    billing_name: '',
    email: '',
    mobile_no: '',
    phone: '',
    whatsapp_no: '',
    contact_person: '',
    contact_person_designation: '',
    contact_person_email: '',
    gstin: '',
    pan: '',
    is_gst_registered: false,
    customer_type: CustomerType.RETAIL,
    customer_group_id: '',
    credit_limit: 0,
    credit_days: 0,
    transport_option: 'Local Delivery',
    transport_details: {},
    assigned_catalogues: [],
    communication_preferences: {
      email_enabled: true,
      sms_enabled: true,
      whatsapp_enabled: false,
      notification_types: {
        order_updates: true,
        payment_reminders: true,
        promotional: false,
      },
    },
    meta: {},
  })

  // Billing name / WhatsApp no. default to mirroring name / mobile, for fast entry
  const [billingNameSameAsName, setBillingNameSameAsName] = useState(true)
  const [whatsappSameAsMobile, setWhatsappSameAsMobile] = useState(true)

  // Optional address data (create mode only - persisted via a separate API call after
  // the customer is created, since POST /admin/customers has no address fields at all)
  const [billingAddress, setBillingAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
  })

  const [shippingAddress, setShippingAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
  })

  const [sameAsBilling, setSameAsBilling] = useState(true)

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  const { data: customer, isLoading: customerLoading } = useGetCustomerQuery(id!, { skip: !isEditing })
  const [createCustomer] = useCreateCustomerMutation()
  const [updateCustomer] = useUpdateCustomerMutation()
  const { data: groupsData } = useGetCustomerGroupsQuery({ page: 1, per_page: 100, is_active: true })
  const { data: cataloguesData } = useGetCataloguesQuery({ page: 1, per_page: 100 })

  // Auto-assign all catalogues when creating new customer
  useEffect(() => {
    if (!isEditing && cataloguesData?.items && (formData.assigned_catalogues || []).length === 0) {
      const allCatalogueIds = cataloguesData.items.map(cat => cat.id)
      setFormData(prev => ({
        ...prev,
        assigned_catalogues: allCatalogueIds
      }))
    }
  }, [cataloguesData, isEditing])

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name,
        billing_name: customer.billing_name,
        email: customer.email,
        mobile_no: customer.mobile_no,
        phone: customer.phone || '',
        whatsapp_no: customer.whatsapp_no || '',
        contact_person: customer.contact_person || '',
        contact_person_designation: customer.contact_person_designation || '',
        contact_person_email: customer.contact_person_email || '',
        gstin: customer.gstin || '',
        pan: customer.pan || '',
        is_gst_registered: customer.is_gst_registered,
        customer_type: customer.customer_type,
        customer_group_id: customer.customer_group_id,
        credit_limit: customer.credit_limit,
        credit_days: customer.credit_days,
        transport_option: customer.transport_option || 'Local Delivery',
        transport_details: customer.transport_details || {},
        assigned_catalogues: customer.assigned_catalogues || [],
        communication_preferences: customer.communication_preferences || formData.communication_preferences,
        meta: customer.meta || {},
      })
    }
  }, [customer])

  // Keep billing name / WhatsApp no. mirrored while the "same as" checkboxes are on (create mode only)
  useEffect(() => {
    if (!isEditing && billingNameSameAsName) {
      setFormData(prev => (prev.billing_name === prev.name ? prev : { ...prev, billing_name: prev.name }))
    }
  }, [formData.name, billingNameSameAsName, isEditing])

  useEffect(() => {
    if (!isEditing && whatsappSameAsMobile) {
      setFormData(prev => (prev.whatsapp_no === prev.mobile_no ? prev : { ...prev, whatsapp_no: prev.mobile_no }))
    }
  }, [formData.mobile_no, whatsappSameAsMobile, isEditing])

  const handleInputChange = (field: keyof CustomerCreateData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const handleBillingAddressChange = (field: string, value: string) => {
    setBillingAddress(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleShippingAddressChange = (field: string, value: string) => {
    setShippingAddress(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  // Step 1: Basic Info
  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.billing_name.trim()) newErrors.billing_name = 'Billing name is required'
    if (!formData.email.trim()) newErrors.email = 'Email is required'
    else if (!isValidEmail(formData.email)) newErrors.email = 'Invalid email address'
    if (!formData.mobile_no.trim()) newErrors.mobile_no = 'Mobile number is required'
    else if (!isValidMobile(formData.mobile_no)) newErrors.mobile_no = 'Invalid mobile number (must start with 6-9, 10 digits)'
    if (!formData.whatsapp_no) newErrors.whatsapp_no = 'WhatsApp number is required'
    else if (!isValidMobile(formData.whatsapp_no)) newErrors.whatsapp_no = 'Invalid WhatsApp number (must start with 6-9, 10 digits)'

    setErrors(prev => ({ ...prev, ...newErrors, ...Object.fromEntries(['name', 'billing_name', 'email', 'mobile_no', 'whatsapp_no'].filter(k => !newErrors[k]).map(k => [k, ''])) }))
    return Object.keys(newErrors).length === 0
  }

  // Step 2: Business Details - customer_group_id is deliberately optional, matching the backend
  // (auto-assigns the Retail group if omitted, see customer_service.py)
  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (formData.is_gst_registered) {
      if (!formData.gstin || !formData.gstin.trim()) {
        newErrors.gstin = 'GSTIN is required for GST registered customers'
      } else if (!isValidGSTIN(formData.gstin)) {
        newErrors.gstin = 'Invalid GSTIN format (e.g., 22AAAAA0000A1Z5)'
      }
      if (!formData.pan || !formData.pan.trim()) {
        newErrors.pan = 'PAN is required for GST registered customers'
      } else if (!isValidPAN(formData.pan)) {
        newErrors.pan = 'Invalid PAN format (e.g., AAAAA0000A)'
      }
    }
    setErrors(prev => ({ ...prev, ...newErrors, ...Object.fromEntries(['gstin', 'pan'].filter(k => !newErrors[k]).map(k => [k, ''])) }))
    return Object.keys(newErrors).length === 0
  }

  // Step 3: Address - entirely optional, but if started, must be completed
  const validateStep3 = (): boolean => {
    const newErrors: Record<string, string> = {}
    const hasAnyBillingField = billingAddress.address_line1 || billingAddress.city || billingAddress.state || billingAddress.pincode
    if (hasAnyBillingField) {
      if (!billingAddress.address_line1.trim()) newErrors.billing_address_line1 = 'Address line 1 is required'
      if (!billingAddress.city.trim()) newErrors.billing_city = 'City is required'
      if (!billingAddress.state.trim()) newErrors.billing_state = 'State is required'
      if (!billingAddress.pincode.trim()) newErrors.billing_pincode = 'Pincode is required'
      else if (!isValidPincode(billingAddress.pincode)) newErrors.billing_pincode = 'Pincode must be 6 digits'
    }
    if (hasAnyBillingField && !sameAsBilling) {
      const hasAnyShippingField = shippingAddress.address_line1 || shippingAddress.city || shippingAddress.state || shippingAddress.pincode
      if (hasAnyShippingField) {
        if (!shippingAddress.address_line1.trim()) newErrors.shipping_address_line1 = 'Address line 1 is required'
        if (!shippingAddress.city.trim()) newErrors.shipping_city = 'City is required'
        if (!shippingAddress.state.trim()) newErrors.shipping_state = 'State is required'
        if (!shippingAddress.pincode.trim()) newErrors.shipping_pincode = 'Pincode is required'
        else if (!isValidPincode(shippingAddress.pincode)) newErrors.shipping_pincode = 'Pincode must be 6 digits'
      }
    }
    setErrors(prev => ({ ...prev, ...newErrors }))
    return Object.keys(newErrors).length === 0
  }

  const validateForm = (): boolean => {
    // Full-form check used as the edit-mode submit gate and the wizard's final safety net
    const step1Valid = validateStep1()
    const step2Valid = validateStep2()
    const step3Valid = isEditing ? true : validateStep3()
    return step1Valid && step2Valid && step3Valid
  }

  const handleNextStep = () => {
    const valid = activeStep === 0 ? validateStep1() : activeStep === 1 ? validateStep2() : validateStep3()
    if (!valid) {
      enqueueSnackbar('Please fix the errors before continuing', { variant: 'warning' })
      return
    }
    setActiveStep(prev => Math.min(prev + 1, WIZARD_STEPS.length - 1))
  }

  const handleBackStep = () => setActiveStep(prev => Math.max(prev - 1, 0))

  const handleSubmit = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' })
      if (!isEditing) setActiveStep(0)
      return
    }

    setLoading(true)
    try {
      const customerPayload: any = { ...formData }

      if (isEditing) {
        await updateCustomer({ id: id!, data: customerPayload }).unwrap()
        enqueueSnackbar('Customer updated successfully', { variant: 'success' })
      } else {
        const created = await createCustomer(customerPayload).unwrap()
        enqueueSnackbar('Customer created successfully', { variant: 'success' })

        // Address is a separate resource (POST /admin/customers/{id}/addresses) - the
        // customer-create endpoint has no address fields and silently drops them.
        const hasBillingAddress = Boolean(
          billingAddress.address_line1 && billingAddress.city && billingAddress.state && billingAddress.pincode
        )
        if (hasBillingAddress) {
          try {
            await AddressService.createAddress({
              customer_id: created.id,
              address_type: sameAsBilling ? AddressType.BOTH : AddressType.BILLING,
              address_line1: billingAddress.address_line1,
              address_line2: billingAddress.address_line2 || undefined,
              city: billingAddress.city,
              state: billingAddress.state,
              postal_code: billingAddress.pincode,
              country: billingAddress.country || 'India',
              is_default: true,
            })

            if (!sameAsBilling) {
              const hasShippingAddress = Boolean(
                shippingAddress.address_line1 && shippingAddress.city && shippingAddress.state && shippingAddress.pincode
              )
              if (hasShippingAddress) {
                await AddressService.createAddress({
                  customer_id: created.id,
                  address_type: AddressType.SHIPPING,
                  address_line1: shippingAddress.address_line1,
                  address_line2: shippingAddress.address_line2 || undefined,
                  city: shippingAddress.city,
                  state: shippingAddress.state,
                  postal_code: shippingAddress.pincode,
                  country: shippingAddress.country || 'India',
                  is_default: false,
                })
              }
            }
          } catch (addrError: any) {
            enqueueSnackbar(
              "Customer created, but the address couldn't be saved. Add it from the customer's Addresses tab.",
              { variant: 'warning' }
            )
          }
        }
      }
      navigate('/admin/parties/customers')
    } catch (error: any) {
      enqueueSnackbar(error?.data?.detail || 'Failed to save customer', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (customerLoading) {
    return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>
  }

  const selectedGroupName = groupsData?.items.find(g => g.id === formData.customer_group_id)?.name

  return (
    <Box>
      <PageHeader
        title={isEditing ? 'Edit Customer' : 'Add Customer'}
        subtitle={isEditing ? 'Update customer details' : 'Guided setup - only Basic Info and Business Details are required'}
        breadcrumbs={[
          { label: 'Party Management', path: '/admin/parties' },
          { label: 'Customers', path: '/admin/parties/customers' },
          { label: isEditing ? 'Edit' : 'Add', path: '' }
        ]}
        actions={isEditing ? [
          {
            label: loading ? 'Saving...' : 'Save Customer',
            onClick: handleSubmit,
            variant: 'contained' as const,
            icon: <SaveIcon />,
            disabled: loading,
          }
        ] : []}
      />

      {isEditing ? (
        <Paper sx={{ mt: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<PersonIcon />} label="Basic Information" iconPosition="start" />
            <Tab icon={<LocationIcon />} label="Address (Optional)" iconPosition="start" />
          </Tabs>

          {/* Tab 1: Basic Information */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Customer Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={Boolean(errors.name)}
                  helperText={errors.name || 'Display name for the customer'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Billing Name"
                  value={formData.billing_name}
                  onChange={(e) => handleInputChange('billing_name', e.target.value)}
                  error={Boolean(errors.billing_name)}
                  helperText={errors.billing_name || 'Name to appear on invoices'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Mobile Number"
                  value={formData.mobile_no}
                  onChange={(e) => handleInputChange('mobile_no', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  error={Boolean(errors.mobile_no)}
                  helperText={errors.mobile_no || 'Primary contact number (10 digits)'}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={Boolean(errors.email)}
                  helperText={errors.email || 'Primary email address'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="WhatsApp Number"
                  value={formData.whatsapp_no}
                  onChange={(e) => handleInputChange('whatsapp_no', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  error={Boolean(errors.whatsapp_no)}
                  helperText={errors.whatsapp_no || 'Required for WhatsApp notifications and OTP'}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Customer Type</InputLabel>
                  <Select
                    value={formData.customer_type}
                    onChange={(e) => handleInputChange('customer_type', e.target.value)}
                    label="Customer Type"
                  >
                    <MenuItem value={CustomerType.RETAIL}>Retail</MenuItem>
                    <MenuItem value={CustomerType.RESELLER}>Reseller</MenuItem>
                    <MenuItem value={CustomerType.WHOLESALER}>Wholesaler</MenuItem>
                    <MenuItem value={CustomerType.BROKER}>Broker</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Determines pricing and product visibility
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(errors.customer_group_id)}>
                  <InputLabel>Customer Group</InputLabel>
                  <Select
                    value={formData.customer_group_id}
                    onChange={(e) => handleInputChange('customer_group_id', e.target.value)}
                    label="Customer Group"
                  >
                    {groupsData?.items.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Applies group-specific rules and pricing. Defaults to Retail if left blank.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>GST Registration Status</InputLabel>
                  <Select
                    value={formData.is_gst_registered ? 'yes' : 'no'}
                    onChange={(e) => {
                      const isRegistered = e.target.value === 'yes'
                      handleInputChange('is_gst_registered', isRegistered)
                      if (!isRegistered) {
                        handleInputChange('gstin', '')
                        handleInputChange('pan', '')
                      }
                    }}
                    label="GST Registration Status"
                  >
                    <MenuItem value="no">Not GST Registered</MenuItem>
                    <MenuItem value="yes">GST Registered</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Select if customer has GST registration
                </Typography>
              </Grid>

              {formData.is_gst_registered && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="GSTIN"
                    value={formData.gstin}
                    onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                    error={Boolean(errors.gstin)}
                    helperText={errors.gstin || 'GST Identification Number (15 characters)'}
                    inputProps={{ maxLength: 15 }}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </Grid>
              )}

              {formData.is_gst_registered && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="PAN"
                    value={formData.pan}
                    onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                    error={Boolean(errors.pan)}
                    helperText={errors.pan || 'Permanent Account Number (10 characters)'}
                    inputProps={{ maxLength: 10 }}
                    placeholder="AAAAA0000A"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Credit Limit"
                  value={formData.credit_limit}
                  onChange={(e) => handleInputChange('credit_limit', parseFloat(e.target.value) || 0)}
                  helperText="Maximum credit amount allowed"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Credit Days"
                  value={formData.credit_days}
                  onChange={(e) => handleInputChange('credit_days', parseInt(e.target.value) || 0)}
                  helperText="Payment due days (0 = immediate payment)"
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={cataloguesData?.items || []}
                  getOptionLabel={(option) => option.name}
                  value={cataloguesData?.items.filter((cat) => (formData.assigned_catalogues || []).includes(cat.id)) || []}
                  onChange={(_, newValue) => {
                    handleInputChange('assigned_catalogues', newValue.map((cat) => cat.id))
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assigned Catalogues"
                      helperText="All catalogues are assigned by default. Remove any you don't want this customer to see."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option.name} {...getTagProps({ index })} />
                    ))
                  }
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 2: Address (Optional) */}
          <TabPanel value={activeTab} index={1}>
            {customer && (
              <AddressManagement
                customerId={customer.id}
                customerName={customer.name}
              />
            )}
          </TabPanel>
        </Paper>
      ) : (
        <Paper sx={{ mt: 3, p: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {WIZARD_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Basic Info */}
          {activeStep === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Fields marked * are required to create the customer.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Customer Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={Boolean(errors.name)}
                  helperText={errors.name || 'Display name for the customer'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Billing Name"
                  value={formData.billing_name}
                  disabled={billingNameSameAsName}
                  onChange={(e) => handleInputChange('billing_name', e.target.value)}
                  error={Boolean(errors.billing_name)}
                  helperText={errors.billing_name || 'Name to appear on invoices'}
                />
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={billingNameSameAsName}
                      onChange={(e) => setBillingNameSameAsName(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Same as customer name</Typography>}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Mobile Number"
                  value={formData.mobile_no}
                  onChange={(e) => handleInputChange('mobile_no', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  error={Boolean(errors.mobile_no)}
                  helperText={errors.mobile_no || 'Primary contact number (10 digits, starts with 6-9)'}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="WhatsApp Number"
                  value={formData.whatsapp_no}
                  disabled={whatsappSameAsMobile}
                  onChange={(e) => handleInputChange('whatsapp_no', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  error={Boolean(errors.whatsapp_no)}
                  helperText={errors.whatsapp_no || 'Required for WhatsApp notifications and OTP'}
                  inputProps={{ maxLength: 10 }}
                />
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={whatsappSameAsMobile}
                      onChange={(e) => setWhatsappSameAsMobile(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Same as mobile number</Typography>}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={Boolean(errors.email)}
                  helperText={errors.email || 'Primary email address'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contact_person}
                  onChange={(e) => handleInputChange('contact_person', e.target.value)}
                  helperText="Optional - primary point of contact"
                />
              </Grid>
            </Grid>
          )}

          {/* Step 1: Business Details */}
          {activeStep === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Customer Type</InputLabel>
                  <Select
                    value={formData.customer_type}
                    onChange={(e) => handleInputChange('customer_type', e.target.value)}
                    label="Customer Type"
                  >
                    <MenuItem value={CustomerType.RETAIL}>Retail</MenuItem>
                    <MenuItem value={CustomerType.RESELLER}>Reseller</MenuItem>
                    <MenuItem value={CustomerType.WHOLESALER}>Wholesaler</MenuItem>
                    <MenuItem value={CustomerType.BROKER}>Broker</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Determines pricing and product visibility
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Customer Group (Optional)</InputLabel>
                  <Select
                    value={formData.customer_group_id}
                    onChange={(e) => handleInputChange('customer_group_id', e.target.value)}
                    label="Customer Group (Optional)"
                  >
                    <MenuItem value="">
                      <em>None - auto-assign to Retail</em>
                    </MenuItem>
                    {groupsData?.items.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Applies group-specific rules and pricing. Defaults to the Retail group if left blank.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>GST Registration Status</InputLabel>
                  <Select
                    value={formData.is_gst_registered ? 'yes' : 'no'}
                    onChange={(e) => {
                      const isRegistered = e.target.value === 'yes'
                      handleInputChange('is_gst_registered', isRegistered)
                      if (!isRegistered) {
                        handleInputChange('gstin', '')
                        handleInputChange('pan', '')
                      }
                    }}
                    label="GST Registration Status"
                  >
                    <MenuItem value="no">Not GST Registered</MenuItem>
                    <MenuItem value="yes">GST Registered</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Select if customer has GST registration
                </Typography>
              </Grid>

              {formData.is_gst_registered && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="GSTIN"
                    value={formData.gstin}
                    onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                    error={Boolean(errors.gstin)}
                    helperText={errors.gstin || 'GST Identification Number (15 characters)'}
                    inputProps={{ maxLength: 15 }}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </Grid>
              )}

              {formData.is_gst_registered && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="PAN"
                    value={formData.pan}
                    onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                    error={Boolean(errors.pan)}
                    helperText={errors.pan || 'Permanent Account Number (10 characters)'}
                    inputProps={{ maxLength: 10 }}
                    placeholder="AAAAA0000A"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Credit Limit"
                  value={formData.credit_limit}
                  onChange={(e) => handleInputChange('credit_limit', parseFloat(e.target.value) || 0)}
                  helperText="Optional - maximum credit amount allowed"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Credit Days"
                  value={formData.credit_days}
                  onChange={(e) => handleInputChange('credit_days', parseInt(e.target.value) || 0)}
                  helperText="Optional - payment due days (0 = immediate payment)"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Delivery Method</InputLabel>
                  <Select
                    value={formData.transport_option}
                    onChange={(e) => handleInputChange('transport_option', e.target.value)}
                    label="Delivery Method"
                  >
                    <MenuItem value="Local Delivery">Local Delivery</MenuItem>
                    <MenuItem value="Via Bus">Via Bus</MenuItem>
                    <MenuItem value="Via Transport">Via Transport</MenuItem>
                    <MenuItem value="Via Courier">Via Courier</MenuItem>
                    <MenuItem value="Self Pickup">Self Pickup</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Optional - preferred delivery method for orders
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={cataloguesData?.items || []}
                  getOptionLabel={(option) => option.name}
                  value={cataloguesData?.items.filter((cat) => (formData.assigned_catalogues || []).includes(cat.id)) || []}
                  onChange={(_, newValue) => {
                    handleInputChange('assigned_catalogues', newValue.map((cat) => cat.id))
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assigned Catalogues"
                      helperText="Optional - all catalogues are assigned by default. Remove any you don't want this customer to see."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option.name} {...getTagProps({ index })} />
                    ))
                  }
                />
              </Grid>
            </Grid>
          )}

          {/* Step 2: Address (Optional) */}
          {activeStep === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  Address is optional. You can skip this step and add it later from the customer's Addresses tab.
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" />
                  Billing Address (Bill To)
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={billingAddress.address_line1}
                  onChange={(e) => handleBillingAddressChange('address_line1', e.target.value)}
                  error={Boolean(errors.billing_address_line1)}
                  helperText={errors.billing_address_line1 || 'Street address, building name, etc.'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  value={billingAddress.address_line2}
                  onChange={(e) => handleBillingAddressChange('address_line2', e.target.value)}
                  helperText="Apartment, suite, unit, etc."
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={billingAddress.city}
                  onChange={(e) => handleBillingAddressChange('city', e.target.value)}
                  error={Boolean(errors.billing_city)}
                  helperText={errors.billing_city}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={billingAddress.state}
                  onChange={(e) => handleBillingAddressChange('state', e.target.value)}
                  error={Boolean(errors.billing_state)}
                  helperText={errors.billing_state}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={billingAddress.pincode}
                  onChange={(e) => handleBillingAddressChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ maxLength: 6 }}
                  error={Boolean(errors.billing_pincode)}
                  helperText={errors.billing_pincode || '6-digit postal code'}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ my: 1, borderTop: 1, borderColor: 'divider' }} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon color="secondary" />
                    Shipping Address (Ship To)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sameAsBilling}
                        onChange={(e) => setSameAsBilling(e.target.checked)}
                      />
                    }
                    label="Same as Billing Address"
                  />
                </Box>
              </Grid>

              {!sameAsBilling && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      value={shippingAddress.address_line1}
                      onChange={(e) => handleShippingAddressChange('address_line1', e.target.value)}
                      error={Boolean(errors.shipping_address_line1)}
                      helperText={errors.shipping_address_line1 || 'Street address, building name, etc.'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 2 (Optional)"
                      value={shippingAddress.address_line2}
                      onChange={(e) => handleShippingAddressChange('address_line2', e.target.value)}
                      helperText="Apartment, suite, unit, etc."
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="City"
                      value={shippingAddress.city}
                      onChange={(e) => handleShippingAddressChange('city', e.target.value)}
                      error={Boolean(errors.shipping_city)}
                      helperText={errors.shipping_city}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value={shippingAddress.state}
                      onChange={(e) => handleShippingAddressChange('state', e.target.value)}
                      error={Boolean(errors.shipping_state)}
                      helperText={errors.shipping_state}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      value={shippingAddress.pincode}
                      onChange={(e) => handleShippingAddressChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputProps={{ maxLength: 6 }}
                      error={Boolean(errors.shipping_pincode)}
                      helperText={errors.shipping_pincode || '6-digit postal code'}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {/* Step 3: Review & Create */}
          {activeStep === 3 && (
            <Stack spacing={3}>
              <Alert severity="info" icon={<ReviewIcon />}>
                Review the details below, then click Create Customer.
              </Alert>

              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Basic Info</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>Name:</strong> {formData.name || '-'}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>Billing Name:</strong> {formData.billing_name || '-'}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>Mobile:</strong> {formData.mobile_no || '-'}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>WhatsApp:</strong> {formData.whatsapp_no || '-'}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>Email:</strong> {formData.email || '-'}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>Contact Person:</strong> {formData.contact_person || <em>Not set</em>}</Typography></Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon fontSize="small" /> Business Details
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>Customer Type:</strong> {formData.customer_type}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>Customer Group:</strong> {selectedGroupName || <em>Auto-assigned (Retail)</em>}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>GST Registered:</strong> {formData.is_gst_registered ? `Yes (${formData.gstin})` : 'No'}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="body2"><strong>Credit Limit / Days:</strong> {formData.credit_limit} / {formData.credit_days}</Typography></Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon fontSize="small" /> Address
                </Typography>
                {billingAddress.address_line1 ? (
                  <Typography variant="body2">
                    {billingAddress.address_line1}{billingAddress.address_line2 ? `, ${billingAddress.address_line2}` : ''}, {billingAddress.city}, {billingAddress.state} - {billingAddress.pincode}
                    {sameAsBilling ? ' (used for billing & shipping)' : ' (billing)'}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary"><em>No address added - can be added later</em></Typography>
                )}
                {!sameAsBilling && shippingAddress.address_line1 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Shipping: {shippingAddress.address_line1}{shippingAddress.address_line2 ? `, ${shippingAddress.address_line2}` : ''}, {shippingAddress.city}, {shippingAddress.state} - {shippingAddress.pincode}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}

          {/* Step navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              disabled={activeStep === 0}
              onClick={handleBackStep}
            >
              Back
            </Button>
            {activeStep < WIZARD_STEPS.length - 1 ? (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={handleNextStep}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Customer'}
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  )
}

export default CustomerFormSimple
